import { useParams } from 'react-router-dom'
import { v4 } from 'uuid'
import {
  SpendingBucket,
  SpendingBucketResponse,
  SpendingBucketCategory,
  Merchant,
  SpendingBucketsTransactionsResponse,
} from '../types'
import { useAcsFetch } from '../hooks/use-acs-fetch'
import { Flags } from '../utils/feature-flags'
import { useFeatureFlags } from '../hooks/use-feature-flags'

export interface SpendingBucketUpdateOptions {
  categoryId: string
  bucketName: string
  bucketTargetAmount: string
  bucketTargetDate?: string
  frequencyCode?: string
  merchantIds?: string[]
  autoDistributeIndicator: boolean
  initialFundAmount?: string
}

interface SpendingBucketDistributeOptions {
  fromBucketId: string
  toBucketId: string
  amount: string
}

interface SpendingBucketTransactionOptions {
  bucketId: string
  startDate?: string
  endDate?: string
  pageNumber: number
}

interface SpendingBucketRecategorizeTransactionOptions {
  fromBucketId: string
  transactionId: string
  toBucketId: string
  reMapMerchantFlag: boolean
}

interface UseSpendingBucketsApiReturn {
  getSpendingBuckets: () => Promise<SpendingBucketResponse>
  createSpendingBucket: (
    options: SpendingBucketUpdateOptions,
  ) => Promise<Record<string, SpendingBucket[]>>
  deleteSpendingBucket: (
    toolkitBucketId: string,
  ) => Promise<Record<string, SpendingBucket[]>>
  getSpendingBucketsCategories: () => Promise<
    Record<string, SpendingBucketCategory[]>
  >
  editSpendingBucket: (
    options: SpendingBucketUpdateOptions,
  ) => Promise<Record<string, SpendingBucket[]>>
  distributeSpendingBucket: (
    options: SpendingBucketDistributeOptions,
  ) => Promise<Record<string, SpendingBucket[]>>
  getMerchants: (merchantName: string) => Promise<Record<string, Merchant[]>>
  getSpendingBucketsTransactions: (
    options: SpendingBucketTransactionOptions,
  ) => Promise<SpendingBucketsTransactionsResponse>
  recategorizeBucketTransaction: (
    options: SpendingBucketRecategorizeTransactionOptions,
  ) => Promise<SpendingBucketResponse>
}

// TO DO: might be able to take this out later if API takes care of dates
/* istanbul ignore next */
const getDefaultStartDate = (): string => {
  const defaultStartDate = new Date()
  // get the date before 120 days from now
  defaultStartDate.setDate(defaultStartDate.getDate() - 120)
  return defaultStartDate.toISOString()
}

export const useSpendingBucketsApi = (): UseSpendingBucketsApiReturn => {
  const { id } = useParams<{ id: string }>()
  const acsFetch = useAcsFetch({
    withCif: true,
    withGuid: true,
    defaultHeaders: {
      ApplicationVersion: '2.9',
      ApplicationID: 'ALLYUSBOLB',
      TraceID: v4(),
      ApplicationName: 'AOB',
    },
  })

  const ffSBVersion = useFeatureFlags(Flags.SBVersion)
  const getSpendingBuckets = async (): Promise<SpendingBucketResponse> => {
    const result = await acsFetch<SpendingBucketResponse>(
      `/${ffSBVersion}/spendbuckets/${id}/buckets`,
    )
    return result.data
  }

  const createSpendingBucket = async (
    options: SpendingBucketUpdateOptions,
  ): Promise<Record<string, SpendingBucket[]>> => {
    const result = await acsFetch<Record<string, SpendingBucket[]>>(
      `/${ffSBVersion}/spendbuckets/${id}/buckets`,
      {
        method: 'POST',
        body: JSON.stringify({ bucket: options }),
      },
    )
    return result.data
  }

  const deleteSpendingBucket = async (
    toolkitBucketId: string,
  ): Promise<Record<string, SpendingBucket[]>> => {
    const response = await acsFetch<Record<string, SpendingBucket[]>>(
      `/${ffSBVersion}/spendbuckets/${id}/buckets/${toolkitBucketId}`,
      {
        method: 'DELETE',
      },
    )

    if (response.data) {
      return response.data
    }

    throw new Error('No deleted bucket in successful response')
  }

  const getSpendingBucketsCategories = async (): Promise<
    Record<string, SpendingBucketCategory[]>
  > => {
    const result = await acsFetch<Record<string, SpendingBucketCategory[]>>(
      `/${ffSBVersion}/spendbuckets/buckets/categories`,
    )
    return result.data
  }

  const editSpendingBucket = async (
    options: SpendingBucketUpdateOptions,
  ): Promise<Record<string, SpendingBucket[]>> => {
    const response = await acsFetch<Record<string, SpendingBucket[]>>(
      `/${ffSBVersion}/spendbuckets/${id}/buckets`,
      {
        method: 'PATCH',
        body: JSON.stringify({ bucket: options }),
      },
    )
    return response.data
  }

  const distributeSpendingBucket = async (
    options: SpendingBucketDistributeOptions,
  ): Promise<Record<string, SpendingBucket[]>> => {
    const response = await acsFetch<Record<string, SpendingBucket[]>>(
      `/${ffSBVersion}/spendbuckets/${id}/distribute`,
      {
        method: 'PATCH',
        body: JSON.stringify(options),
      },
    )
    return response.data
  }

  const getMerchants = async (
    merchantName: string,
  ): Promise<Record<string, Merchant[]>> => {
    const response = await acsFetch<Record<string, Merchant[]>>(
      `/${ffSBVersion}/spendbuckets/${id}/merchants?merchantName=${merchantName}`,
    )
    return response.data
  }

  const getSpendingBucketsTransactions = async (
    options: SpendingBucketTransactionOptions,
  ): Promise<SpendingBucketsTransactionsResponse> => {
    const nextDay = new Date()
    nextDay.setDate(nextDay.getDate() + 1)
    const startDate = options.startDate || getDefaultStartDate()
    const endDate = options.endDate || nextDay.toISOString()
    const response = await acsFetch<{
      bucket: SpendingBucketsTransactionsResponse
    }>(
      `/${ffSBVersion}/spendbuckets/${id}/buckets/${
        options.bucketId
      }/transactions?startDate=${startDate}&endDate=${endDate}&recordsPerPage=20&pageNumber=${
        options.pageNumber - 1
      }`,
    )
    return response.data.bucket
  }

  const recategorizeBucketTransaction = async (
    options: SpendingBucketRecategorizeTransactionOptions,
  ): Promise<SpendingBucketResponse> => {
    const response = await acsFetch<{
      bucketsResponse: { buckets: SpendingBucket[]; customBucketTotal: number }
    }>(`/${ffSBVersion}/spendbuckets/${id}/buckets/transactions/recategorize`, {
      method: 'PATCH',
      body: JSON.stringify({
        fromBucketId: options.fromBucketId,
        toBucketId: options.toBucketId,
        reMapMerchantFlag: options.reMapMerchantFlag,
        transactionId: options.transactionId,
      }),
    })
    return response.data.bucketsResponse
  }

  return {
    createSpendingBucket,
    deleteSpendingBucket,
    getSpendingBuckets,
    getSpendingBucketsCategories,
    editSpendingBucket,
    distributeSpendingBucket,
    getMerchants,
    getSpendingBucketsTransactions,
    recategorizeBucketTransaction,
  }
}
